import React, { useMemo } from "react";
// react-chartjs-2 v3'te `Chart` adlı dışa aktarım chart.js sınıfının kendisi;
// karışık (bar + line) grafik için genel React bileşeni varsayılan dışa aktarımdır.
import ChartComponent from "react-chartjs-2";
import { T, useTheme } from "Components";

import useStatistics from "../../Infrastructure/useStatistics";
import { AxisStyle, ReadChartPalette, TooltipStyle } from "../../Infrastructure/chartSetup";
import { FormatCurrency, FormatNumber } from "../../Infrastructure/format";

/**
 * Üye ve Tahsilat Trendi — son 12 ay.
 * Bar: aylık tahsilat, çizgi: aktif üye (ikinci eksen).
 */
const TrendChart = () => {
  const { data, loading, error } = useStatistics("/Statistics/Trend", []);
  const { theme } = useTheme();

  const points = Array.isArray(data) ? data : [];

  // Tema değişiminde renkler yeniden okunur → grafik yeniden çizilir
  const { chartData, options } = useMemo(() => {
    const palette = ReadChartPalette();

    return {
      chartData: {
        labels: points.map((point) => point.Label),
        datasets: [
          {
            type: "bar",
            label: T.Main("Dashboard.Statistics.Collection"),
            data: points.map((point) => point.CollectedAmount),
            backgroundColor: palette.primary,
            borderRadius: 4,
            borderSkipped: "bottom",
            barPercentage: 0.46,
            categoryPercentage: 1,
            order: 2,
            yAxisID: "y",
          },
          {
            type: "line",
            label: T.Main("Dashboard.Statistics.ActiveMember"),
            data: points.map((point) => point.ActiveMemberCount),
            borderColor: palette.teal,
            backgroundColor: palette.teal,
            borderWidth: 2.4,
            tension: 0.35,
            pointRadius: 3,
            pointHoverRadius: 5,
            order: 1,
            yAxisID: "y1",
          },
        ],
      },
      options: {
        responsive: true,
        interaction: { mode: "index", intersect: false },
        layout: { padding: { top: 6 } },
        plugins: {
          legend: { display: false },
          tooltip: {
            ...TooltipStyle(palette),
            callbacks: {
              label: (context) =>
                context.dataset.yAxisID === "y1"
                  ? `${context.dataset.label}: ${FormatNumber(context.parsed.y)}`
                  : `${context.dataset.label}: ${FormatCurrency(context.parsed.y)}`,
            },
          },
        },
        scales: {
          x: AxisStyle(palette, { grid: false }),
          y: {
            ...AxisStyle(palette),
            beginAtZero: true,
            ticks: {
              ...AxisStyle(palette).ticks,
              callback: (value) => FormatNumber(value),
            },
          },
          y1: {
            ...AxisStyle(palette, { grid: false }),
            position: "right",
            ticks: {
              ...AxisStyle(palette).ticks,
              callback: (value) => FormatNumber(value),
            },
          },
        },
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, theme]);

  return (
    <section className="fdash__card fdash__chart">
      <header className="fdash__card__head">
        <div className="fdash__card__title">
          <b>{T.Main("Dashboard.Statistics.MemberCollectionTrend")}</b>
          <span>{T.Main("Dashboard.Statistics.Last12Months")}</span>
        </div>
        <div className="fdash__legend">
          <span>
            <i style={{ background: "var(--primary)" }} />
            {T.Main("Dashboard.Statistics.Collection")}
          </span>
          <span>
            <i style={{ background: "var(--teal)" }} />
            {T.Main("Dashboard.Statistics.ActiveMember")}
          </span>
        </div>
      </header>

      {error && (
        <div className="fdash__state fdash__state--error">
          {T.Main("Dashboard.Statistics.LoadError")}
        </div>
      )}

      {!error && loading && (
        <div className="fdash__card__body">
          <div className="fdash__skeleton" style={{ height: 250 }} />
        </div>
      )}

      {!error && !loading && points.length === 0 && (
        <div className="fdash__state">{T.Main("Dashboard.Statistics.NoData")}</div>
      )}

      {!error && !loading && points.length > 0 && (
        <div className="fdash__chart__canvas">
          <ChartComponent type="bar" data={chartData} options={options} />
        </div>
      )}
    </section>
  );
};

export default TrendChart;
